import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Send,
  Mail,
  MessageSquare,
  Users,
  Filter,
  CheckCircle2,
  Clock,
  AlertCircle,
  Search,
  Inbox,
  ChevronRight
} from 'lucide-react';

// Mock de mensajes enviados
const mockSentMessages = [
  {
    id: '1',
    subject: 'Recordatorio: Tu membresía vence en 15 días',
    content: 'Estimado afiliado, te recordamos que tu membresía UPIM está próxima a vencer...',
    channel: 'email',
    recipients: 45,
    sentAt: '2025-07-20T10:30:00',
    status: 'delivered',
    segment: 'expiring'
  },
  {
    id: '2',
    subject: 'Nuevo evento: Real Estate Technology 2025',
    content: 'Te invitamos a participar en el evento más importante del sector inmobiliario...',
    channel: 'email',
    recipients: 1890,
    sentAt: '2025-07-18T15:00:00',
    status: 'delivered',
    segment: 'all'
  },
  {
    id: '3',
    subject: 'Master Class mañana - No faltes',
    content: 'Recordatorio de la Master Class de mañana sobre Marketing Digital...',
    channel: 'whatsapp',
    recipients: 520,
    sentAt: '2025-07-17T18:00:00',
    status: 'delivered',
    segment: 'active'
  },
];

const AdminMensajes = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [messageForm, setMessageForm] = useState({
    subject: '',
    content: '',
    channel: 'email',
    segment: 'all'
  });

  const segments = [
    { value: 'all', label: 'Todos los afiliados', count: 2150 },
    { value: 'active', label: 'Afiliados activos', count: 1890 },
    { value: 'expiring', label: 'Membresías por vencer', count: 45 },
    { value: 'expired', label: 'Membresías vencidas', count: 180 },
    { value: 'providers', label: 'Proveedores certificados', count: 48 },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Entregado</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Fallido</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  const getChannelIcon = (channel) => {
    return channel === 'whatsapp' ? MessageSquare : Mail;
  };

  const handleSendMessage = () => {
    if (messageForm.subject && messageForm.content) {
      alert(`Mensaje enviado a ${segments.find(s => s.value === messageForm.segment)?.count} destinatarios`);
      setMessageForm({ subject: '', content: '', channel: 'email', segment: 'all' });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-mensajes-title">
              Centro de Mensajes
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Envía comunicaciones a afiliados y proveedores
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2">
          <Button
            variant={activeTab === 'compose' ? 'default' : 'outline'}
            onClick={() => setActiveTab('compose')}
            className={activeTab === 'compose' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
            data-testid="tab-compose"
          >
            <Send className="h-4 w-4 mr-2" />
            Redactar Mensaje
          </Button>
          <Button
            variant={activeTab === 'sent' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sent')}
            className={activeTab === 'sent' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
            data-testid="tab-sent"
          >
            <Inbox className="h-4 w-4 mr-2" />
            Mensajes Enviados
          </Button>
        </div>

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Message Form */}
            <Card className="lg:col-span-2 border border-gray-100">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Nuevo Mensaje</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="subject">Asunto *</Label>
                  <Input
                    id="subject"
                    value={messageForm.subject}
                    onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                    placeholder="Asunto del mensaje"
                    className="mt-1"
                    data-testid="input-subject"
                  />
                </div>
                
                <div>
                  <Label htmlFor="content">Contenido del mensaje *</Label>
                  <Textarea
                    id="content"
                    value={messageForm.content}
                    onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                    placeholder="Escribe el contenido de tu mensaje aquí..."
                    className="mt-1 min-h-[200px]"
                    data-testid="input-content"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Variables disponibles: {'{nombre}'}, {'{email}'}, {'{plan}'}, {'{fecha_vencimiento}'}
                  </p>
                </div>

                {/* Channel Selection */}
                <div>
                  <Label>Canal de envío</Label>
                  <div className="flex space-x-3 mt-2">
                    <Button
                      type="button"
                      variant={messageForm.channel === 'email' ? 'default' : 'outline'}
                      onClick={() => setMessageForm({ ...messageForm, channel: 'email' })}
                      className={messageForm.channel === 'email' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      type="button"
                      variant={messageForm.channel === 'whatsapp' ? 'default' : 'outline'}
                      onClick={() => setMessageForm({ ...messageForm, channel: 'whatsapp' })}
                      className={messageForm.channel === 'whatsapp' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    className="bg-gray-900 hover:bg-gray-800"
                    onClick={handleSendMessage}
                    disabled={!messageForm.subject || !messageForm.content}
                    data-testid="btn-send-message"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Mensaje
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Segment Selection */}
            <Card className="border border-gray-100">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Destinatarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {segments.map((segment) => (
                    <button
                      key={segment.value}
                      onClick={() => setMessageForm({ ...messageForm, segment: segment.value })}
                      className={`
                        w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left
                        ${messageForm.segment === segment.value 
                          ? 'bg-gray-900 text-white border-gray-900' 
                          : 'bg-white border-gray-200 hover:bg-gray-50'}
                      `}
                      data-testid={`segment-${segment.value}`}
                    >
                      <div className="flex items-center space-x-3">
                        <Users className={`h-5 w-5 ${messageForm.segment === segment.value ? 'text-white' : 'text-gray-500'}`} />
                        <span className="text-sm font-medium">{segment.label}</span>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className={messageForm.segment === segment.value ? 'bg-white/20 text-white' : 'bg-gray-100'}
                      >
                        {segment.count.toLocaleString()}
                      </Badge>
                    </button>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Resumen de envío</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Canal:</span> {messageForm.channel === 'email' ? 'Email' : 'WhatsApp'}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Destinatarios:</span> {segments.find(s => s.value === messageForm.segment)?.count.toLocaleString()}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Segmento:</span> {segments.find(s => s.value === messageForm.segment)?.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sent Messages Tab */}
        {activeTab === 'sent' && (
          <Card className="border border-gray-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Historial de Mensajes</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar mensajes..." className="pl-9 h-9" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockSentMessages.map((message) => {
                  const ChannelIcon = getChannelIcon(message.channel);
                  return (
                    <div 
                      key={message.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`message-${message.id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          message.channel === 'whatsapp' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <ChannelIcon className={`h-5 w-5 ${
                            message.channel === 'whatsapp' ? 'text-green-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{message.subject}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{message.content}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-900">{message.recipients.toLocaleString()} destinatarios</p>
                          <p className="text-xs text-gray-500">
                            {new Date(message.sentAt).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {getStatusBadge(message.status)}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border border-gray-100">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">98.5%</p>
                  <p className="text-sm text-gray-500">Tasa de entrega</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">156</p>
                  <p className="text-sm text-gray-500">Mensajes este mes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">12</p>
                  <p className="text-sm text-gray-500">Pendientes de envío</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMensajes;
